import { Draggable, type DropResult } from "@hello-pangea/dnd";
import { CalendarClock, Plus } from "lucide-react";
import { useLocaleState, useNotify, useTranslate, useUpdate } from "ra-core";
import { useMemo, type CSSProperties } from "react";
import { useSearchParams } from "react-router";

import {
  KanbanBoard,
  KanbanColumn,
  KanbanDropZone,
} from "@/components/atomic-crm/misc/Kanban";
import { PlatformBadge } from "@/components/competitors/PlatformIcon";
import { SELECT_CLASS } from "@/components/market-intel/FormDialog";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatDate } from "@/lib/dates";
import { cn } from "@/lib/utils";

import "@/styles/mobile-glass.css";

import { pieceDate } from "./calendar";
import {
  CONTENT_STATUSES,
  isContentStatus,
  statusLabelKey,
  STATUS_COLOR,
} from "./constants";
import { useMarketing } from "./context";
import { findById } from "./hooks";
import {
  filterPieces,
  groupPiecesByStatus,
  isOverdue,
  movePiece,
} from "./pipeline";
import type { ContentItem, ContentStatus } from "./types";
import { FormatChip, PillarChips, SaleAvatar } from "./ui";

/**
 * Marketing > Pipeline: pieces by status (idea -> publicado) on the shared
 * kanban. Drag to change status / order. On a phone, one column at a time
 * picked with glass chips (like Deals).
 */
export const PipelineView = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const { data, newPiece } = useMarketing();
  const [update] = useUpdate();
  const channel = params.get("canal") ?? "";
  const plan = params.get("plan") ?? "";
  const columnParam = params.get("columna");
  const mobileColumn: ContentStatus = isContentStatus(columnParam)
    ? columnParam
    : "idea";

  const setParam = (key: string, value: string) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );

  const columns = useMemo(
    () => groupPiecesByStatus(filterPieces(data.items, { channel, plan })),
    [data.items, channel, plan],
  );

  const onDragEnd = ({ draggableId, destination }: DropResult) => {
    if (!destination || !isContentStatus(destination.droppableId)) return;
    const patches = movePiece(
      columns,
      draggableId,
      destination.droppableId,
      destination.index,
      new Date().toISOString(),
    );
    const moved = patches.find(
      (patch) => String(patch.id) === draggableId && patch.data.status,
    );
    patches.forEach((patch) =>
      update(
        "content_items",
        { id: patch.id, data: patch.data, previousData: patch.previousData },
        {
          mutationMode: "optimistic",
          onError: () => notify("marketing.pipeline.error", { type: "error" }),
        },
      ),
    );
    if (moved?.data.status)
      notify("marketing.pipeline.moved", {
        type: "success",
        messageArgs: { status: translate(statusLabelKey(moved.data.status)) },
      });
  };

  const statuses = isMobile ? [mobileColumn] : CONTENT_STATUSES;

  return (
    <section className="space-y-3 md:space-y-4">
      <h2 className="sr-only">{translate("marketing.pipeline.heading")}</h2>
      <div className="flex items-center gap-2">
        <label htmlFor="mk-pipe-channel" className="sr-only">
          {translate("marketing.common.filter_channel")}
        </label>
        <select
          id="mk-pipe-channel"
          className={cn(
            SELECT_CLASS,
            "h-9 min-w-0 flex-1 rounded-full sm:w-48 sm:flex-none",
          )}
          value={channel}
          onChange={(event) => setParam("canal", event.target.value)}
        >
          <option value="">
            {translate(
              isMobile
                ? "marketing.common.channels_short"
                : "marketing.common.all_channels",
            )}
          </option>
          {data.channels.map((entry) => (
            <option key={entry.id} value={String(entry.id)}>
              {entry.name}
            </option>
          ))}
        </select>
        <label htmlFor="mk-pipe-plan" className="sr-only">
          {translate("marketing.common.filter_plan")}
        </label>
        <select
          id="mk-pipe-plan"
          className={cn(
            SELECT_CLASS,
            "h-9 min-w-0 flex-1 rounded-full sm:w-64 sm:flex-none",
          )}
          value={plan}
          onChange={(event) => setParam("plan", event.target.value)}
        >
          <option value="">
            {translate(
              isMobile
                ? "marketing.common.plans_short"
                : "marketing.common.all_plans",
            )}
          </option>
          {data.plans.map((entry) => (
            <option key={entry.id} value={String(entry.id)}>
              {entry.name}
            </option>
          ))}
        </select>
        <Button
          type="button"
          onClick={() =>
            newPiece({
              status: isMobile ? mobileColumn : "idea",
              channel_id: channel,
              plan_id: plan,
            })
          }
          aria-label={translate("marketing.common.new_piece")}
          className="btn-save ml-auto h-9 shrink-0 rounded-full max-sm:size-9 max-sm:p-0 sm:px-4"
        >
          <Plus aria-hidden className="size-4" />
          <span className="max-sm:sr-only">
            {translate("marketing.common.new_piece")}
          </span>
        </Button>
      </div>
      {isMobile ? (
        <div
          role="radiogroup"
          aria-label={translate("marketing.pipeline.status_label")}
          className="mi-pills"
        >
          {CONTENT_STATUSES.map((status) => (
            <button
              key={status}
              type="button"
              role="radio"
              aria-checked={status === mobileColumn}
              data-active={status === mobileColumn ? "true" : undefined}
              ref={(node) => {
                if (node && status === mobileColumn)
                  node.scrollIntoView?.({ inline: "center", block: "nearest" });
              }}
              className="glass-chip"
              onClick={() =>
                setParam("columna", status === "idea" ? "" : status)
              }
            >
              <span
                aria-hidden
                className="glass-chip__dot"
                style={{ background: STATUS_COLOR[status] }}
              />
              {translate(statusLabelKey(status))}
              <span className="glass-chip__count">
                {columns[status].length}
              </span>
            </button>
          ))}
        </div>
      ) : null}
      <KanbanBoard
        onDragEnd={onDragEnd}
        className={cn("mk-board", isMobile && "mk-board--single")}
      >
        {statuses.map((status) => (
          <KanbanColumn
            key={status}
            title={translate(statusLabelKey(status))}
            count={columns[status].length}
            color={STATUS_COLOR[status]}
          >
            <KanbanDropZone
              droppableId={status}
              isEmpty={columns[status].length === 0}
              emptyLabel={translate("marketing.pipeline.empty_column")}
            >
              {columns[status].map((piece, index) => (
                <Draggable
                  key={piece.id}
                  draggableId={String(piece.id)}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className={cn(
                        "pb-2",
                        snapshot.isDragging && "rotate-[1.5deg]",
                      )}
                    >
                      <PipelineCard piece={piece} index={index} />
                    </div>
                  )}
                </Draggable>
              ))}
            </KanbanDropZone>
          </KanbanColumn>
        ))}
      </KanbanBoard>
    </section>
  );
};

const PipelineCard = ({
  piece,
  index,
}: {
  piece: ContentItem;
  index: number;
}) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const { data, sales, openPiece } = useMarketing();
  const channel = findById(data.channels, piece.channel_id);
  const assignee = findById(sales, piece.assignee_id);
  const siblings = data.pillars.filter(
    (pillar) => String(pillar.plan_id) === String(piece.plan_id),
  );
  const pillars = [piece.pillar_id, piece.secondary_pillar_id]
    .map((id) => findById(data.pillars, id))
    .filter((pillar) => pillar != null);
  const date = pieceDate(piece);
  const overdue = isOverdue(piece, new Date());

  return (
    <button
      type="button"
      onClick={() => openPiece(piece.id)}
      className="mk-card mi-rise"
      style={{ "--mi-index": index } as CSSProperties}
    >
      <span className="flex items-start gap-2">
        {channel ? (
          <PlatformBadge platform={channel.platform} size="sm" />
        ) : null}
        <span className="min-w-0 flex-1 text-left text-sm leading-snug font-medium [overflow-wrap:anywhere]">
          {piece.title}
        </span>
        <SaleAvatar sale={assignee} />
      </span>
      <span className="flex flex-wrap items-center gap-1.5">
        <FormatChip format={piece.format} />
        <PillarChips pillars={pillars} siblings={siblings} />
        {date ? (
          <span
            className={cn(
              "inline-flex items-center gap-1 text-xs text-muted-foreground tabular-nums",
              overdue && "mk-overdue",
            )}
          >
            <CalendarClock aria-hidden className="size-3.5" />
            {formatDate(date, locale, {
              day: "numeric",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
            })}
            {overdue ? (
              <span className="sr-only">
                {" "}
                {translate("marketing.common.overdue")}
              </span>
            ) : null}
          </span>
        ) : null}
      </span>
    </button>
  );
};
